import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';

const toolName = 'seo-audit-page-links' as const;

export const brokenLinkSchema = z.object({
  url: z.string().describe('The href attribute of the broken link'),
  linkMode: z
    .enum(['page', 'section', 'dynpage'])
    .describe('The type of internal link'),
  sourcePageId: z.string().describe('The page ID where the broken link lives'),
  pageId: z.string().optional().describe('The target page ID (for page links)'),
  elementId: z
    .string()
    .optional()
    .describe('The DOM node ID containing the broken link'),
  errorType: z.string().describe('The type of error (e.g. page_not_found)'),
  errorMessage: z.string().describe('Human-readable error description'),
  localeId: z
    .string()
    .optional()
    .describe('The locale ID where this link is broken'),
});

export type BrokenLink = z.infer<typeof brokenLinkSchema>;

export const auditPageLinksArgsSchema = z.object({
  siteId: z.string().describe('The ID of the site'),
  pageId: z.string().describe('The ID of the page to audit'),
});

export type AuditPageLinksArgs = z.infer<typeof auditPageLinksArgsSchema>;

export const auditPageLinksResultSchema = z.object({
  pageId: z.string(),
  totalLinksChecked: z.number(),
  brokenLinks: z.array(brokenLinkSchema),
  errors: z.array(z.string()),
});

export type AuditPageLinksResult = z.infer<typeof auditPageLinksResultSchema>;

export const auditPageLinksManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Audit page links',
  prettyStatus: {
    executed: 'Audited page for broken links',
    executing: 'Auditing page for broken links',
    failed: 'Failed to audit page links',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Audits a single page for broken internal links by scanning its DOM nodes.
      Checks all elements with link settings (not just Link elements) for references
      to deleted or archived pages.
    `,
    inputSchema: auditPageLinksArgsSchema,
  },
  type: 'server',
  permission: [{resource: 'SiteLink', action: 'manage'}],
  confirmBeforeExecuting: false,
  timeout: 60000,
});
